<script
  generic="
    TOption,
    TMulti extends boolean | undefined,
    TKey extends keyof TOption | ((option: TOption) => any) | undefined,
    TValue extends (TMulti extends true
      ? Array<TOption | ReturnType<Extract<TKey, (option: TOption) => any>>>
      : TOption | ReturnType<Extract<TKey, (option: TOption) => any>>)
  "
  setup
  lang="ts"
>
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useApplicationRoot } from '@tools/applicationRoot';
import AutoComplete, {
  type AutoCompleteCompleteEvent,
  type AutoCompleteEmitsOptions,
  type AutoCompleteProps,
} from 'primevue/autocomplete';
import MultiSelect, {
  type MultiSelectEmitsOptions,
  type MultiSelectProps,
} from 'primevue/multiselect';
import Select, { type SelectEmitsOptions, type SelectProps } from 'primevue/select';
import { type ComponentPublicInstance, computed, ref, type Ref, useTemplateRef, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    id?: string;
    dataTestid?: string;
    options?: TOption[];
    optionLabel?: keyof TOption | ((option: TOption) => string);
    optionValue?: keyof TOption;
    multiple?: TMulti | boolean;
    searchable?: boolean;
    searchFn?: (event: AutoCompleteCompleteEvent) => Promise<TOption[]>;
    loading?: boolean | undefined;
    disabled?: boolean;
    readonly?: boolean;
    invalid?: boolean;
    placeholder?: string;
    autoCompleteDropdown?: boolean;
    forceSelection?: AutoCompleteProps['forceSelection'];
    size?: 'sm' | 'lg';
    virtualScrollerOptions?: AutoCompleteProps['virtualScrollerOptions'];
  }>(),
  {
    loading: undefined,
    multiple: false,
    autoCompleteDropdown: true,
    forceSelection: true,
  },
);

const rootRef = useTemplateRef<ComponentPublicInstance>('root');

const primevueSize = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'small';
    case 'lg':
      return 'large';
    default:
      return undefined;
  }
});

const model = defineModel<TValue | null>({ default: null });
const shadowModel = ref<TValue | null>() as Ref<TValue>;
const applicationRoot = useApplicationRoot();

const autoCompleteQuery = ref('');
const autoCompleteSuggestions = ref([]) as Ref<TOption[]>;
const searchFunctionRunning = ref(false);
const overlayVisible = ref(false);
const searchFunctionTokens: boolean[] = [];
const dataTestId = computed(() =>
  props.dataTestid ? `${props.dataTestid}` : 'select-field-input',
);

const shouldHideEmptyOverlay = computed(
  () =>
    !props.forceSelection &&
    autoCompleteQuery.value.length > 0 &&
    autoCompleteSuggestions.value.length === 0,
);

const component = computed<typeof AutoComplete | typeof MultiSelect | typeof Select>(() => {
  if (typeof props.searchFn === 'function' || props.searchable) {
    return AutoComplete;
  }
  if (props.multiple) {
    return MultiSelect;
  }
  return Select;
});

type AutoCompleteBindable = AutoCompleteProps & Partial<AutoCompleteEmitsOptions>;
type MultiSelectBindable = MultiSelectProps & Partial<MultiSelectEmitsOptions>;
type SelectBindable = SelectProps & Partial<SelectEmitsOptions>;
const componentProps = computed<AutoCompleteBindable | MultiSelectBindable | SelectBindable>(() => {
  const baseProps = {
    optionLabel: props.optionLabel,
    optionValue: props.optionValue,
    disabled: props.disabled,
    invalid: props.invalid,
    placeholder: props.placeholder,
    overlayClass: `z-2200! ${shouldHideEmptyOverlay.value ? 'hidden!' : ''}`,
    appendTo: applicationRoot,
  };
  switch (component.value) {
    case AutoComplete:
      return {
        ...baseProps,
        completeOnFocus: !shouldHideEmptyOverlay.value,
        focusOnHover: false,
        inputId: props.id,
        suggestions: autoCompleteSuggestions.value,
        multiple: props.multiple ?? false,
        forceSelection: props.forceSelection,
        onComplete: doSearch,
        onShow: () => {
          overlayVisible.value = true;
        },
        onHide: () => {
          overlayVisible.value = false;
          autoCompleteSuggestions.value = [];
        },
        onBlur: () => (searchFunctionRunning.value = false),
        onKeydown: (event: KeyboardEvent) => {
          if ((event.key === 'Enter' || event.key === 'NumpadEnter') && overlayVisible.value) {
            event.preventDefault();
          }
        },
        onOptionSelect: handleAutoCompleteOptionChanged,
        onOptionUnselect: handleAutoCompleteOptionChanged,
        'onUpdate:modelValue': checkInput,
        dropdown: props.autoCompleteDropdown,
        virtualScrollerOptions: props.virtualScrollerOptions,
      } as AutoCompleteBindable;
    case MultiSelect:
      return {
        ...baseProps,
        dataTestid: props.dataTestid ?? 'select-field-input',
        inputId: props.id,
        options: props.options,
        onChange: () => (model.value = shadowModel.value),
        showToggleAll: false,
      } as MultiSelectBindable;
    case Select:
    default:
      return {
        ...baseProps,
        'aria-label': props.id,
        labelId: props.id,
        inputId: props.id,
        id: props.id,
        options: props.options,
        onChange: () => (model.value = shadowModel.value),
      } as SelectBindable;
  }
});

const getFocusInput = (): HTMLInputElement | null => rootRef.value?.$el.querySelector('input');

function isTOption(option: TOption | TKey): option is TOption {
  return (
    !props.optionValue &&
    typeof option === 'object' &&
    option !== null &&
    option.constructor === Object
  );
}

function isTKey(option: TOption | TKey): option is TKey {
  return !!props.optionValue && typeof option !== 'object';
}

function findByProperty(
  array: TOption[],
  property: keyof TOption,
  value: TKey,
): TOption | undefined {
  return array.find((item) => item[property] === value);
}

const findOptionByValue = (value: TValue | null) =>
  props.optionValue
    ? (
        props.options ??
        (autoCompleteSuggestions.value.length
          ? autoCompleteSuggestions.value
          : Array.isArray(shadowModel.value)
            ? shadowModel.value
            : [])
      ).find(({ [props.optionValue as string]: optionValue }) => optionValue === value)
    : undefined;

function getStringLabel(value: any) {
  if (typeof value === 'string') return value;
  const { toString } = value as { toString?: () => string };
  return toString ? toString.apply(value) : null;
}

const getLabel = (option: TOption | TKey): string | null => {
  if (
    option &&
    props.optionLabel &&
    props.optionValue &&
    typeof props.optionValue !== 'function' &&
    isTKey(option)
  ) {
    const selectedOption = findByProperty(props.options ?? [], props.optionValue, option);
    if (selectedOption) {
      if (typeof props.optionLabel === 'function') {
        return props.optionLabel(selectedOption);
      }

      const readonlyValue = selectedOption[props.optionLabel];
      return getStringLabel(readonlyValue);
    }
  }

  if (typeof option === 'string') return option;

  if (!props.optionLabel || !option) return null;
  if (typeof props.optionLabel === 'function' && isTOption(option))
    return props.optionLabel(option);

  if (
    typeof props.optionLabel !== 'function' &&
    typeof option === 'object' &&
    !!option &&
    option.constructor === Object
  ) {
    const label = option[props.optionLabel];
    return getStringLabel(label);
  }
  return null;
};

function getValue(value: TValue | null) {
  return component.value === AutoComplete && props.optionValue
    ? props.multiple && Array.isArray(value)
      ? value.map(findOptionByValue)
      : findOptionByValue(value)
    : value;
}

const readonlyValue = computed(() => {
  if (props.multiple && Array.isArray(shadowModel.value)) {
    if (shadowModel.value.length === 0) return '<geen geselecteerd>';
    return shadowModel.value.map((x) => getLabel(x)).join(', ');
  }

  // readonly handling for single value
  return getLabel(shadowModel.value as unknown as TOption);
});

function doSearch(event: AutoCompleteCompleteEvent) {
  autoCompleteQuery.value = event.query.toLowerCase();
  if (props.searchFn === undefined) {
    searchThroughOptions(event);
    return;
  }

  // run props.searchFn and keep a list of 'function tokens', in case the searchFn is executed multiple times
  // this ensures that searchFunctionRunning remains true until all instances of searchFn have been completed
  const tokenIndex = searchFunctionTokens.length;
  searchFunctionTokens[tokenIndex] = false;
  searchFunctionRunning.value = true;
  const searchQuery = autoCompleteQuery.value;

  const searchFnPromise = props.searchFn(event);
  searchFnPromise
    .then((searchResult) => {
      const value = props.multiple ? searchQuery : shadowModel.value;
      if (
        searchQuery === getLabel(value as unknown as TOption)?.toLowerCase() ||
        (searchQuery === '' && searchFunctionRunning.value)
      ) {
        autoCompleteSuggestions.value = searchResult;
      }
    })
    .finally(() => {
      searchFunctionTokens[tokenIndex] = true;
      if (searchFunctionTokens.every((token) => token)) {
        searchFunctionRunning.value = false;
        searchFunctionTokens.splice(0, searchFunctionTokens.length);
      }
    });
}

function handleAutoCompleteOptionChanged() {
  model.value = props.optionValue
    ? props.multiple && Array.isArray(shadowModel.value) && !!props.optionValue
      ? shadowModel.value.map((option) => option[props.optionValue])
      : typeof shadowModel.value !== 'function' && typeof props.optionValue !== 'function'
        ? // @ts-expect-error nodig omdat TValue generiek is en dit volgens TS eigenlijk niet kan
          shadowModel.value[props.optionValue]
        : shadowModel.value
    : shadowModel.value;
}

function searchThroughOptions(event: { query: string }): void {
  const query = event.query.toLowerCase();
  if (!Array.isArray(props.options)) {
    return;
  }

  if (query.length === 0) {
    autoCompleteSuggestions.value = props.options;
    return;
  }

  autoCompleteSuggestions.value = props.options.filter((option) =>
    getLabel(option)?.toLowerCase().includes(query),
  );
}

function checkInput(input: string) {
  const selectedOption = props.options?.find((option) => getLabel(option) === input);

  if (!input && selectedOption === undefined) {
    model.value = selectedOption as TValue;
    return;
  }

  if (selectedOption !== undefined) {
    if (props.multiple && Array.isArray(model.value)) {
      model.value.push(selectedOption);
    } else {
      model.value = selectedOption as TValue;
    }
  } else if (!props.forceSelection && input) {
    model.value = input as TValue;
  }
}

watch(
  model,
  (value) => {
    shadowModel.value = getValue(value);
  },
  { immediate: true },
);

watch(
  () => props.options,
  () => {
    if (
      component.value === AutoComplete &&
      props.optionValue &&
      typeof shadowModel.value !== 'string'
    ) {
      shadowModel.value = getValue(model.value);
    }
  },
  { deep: true },
);

watch(
  () => props.options,
  () => {
    if (component.value === AutoComplete && !!props.searchFn) {
      autoCompleteSuggestions.value = props.options || [];
    }
  },
  { immediate: true, deep: true },
);

defineExpose({
  blur: () => getFocusInput()?.blur(),
  focus: () => getFocusInput()?.focus(),
  isFocused: () => document.activeElement === getFocusInput(),
});
</script>

<template>
  <component
    :data-testid="dataTestId"
    ref="root"
    v-if="!props.readonly && !!component && !!componentProps"
    :is="component"
    v-bind="componentProps"
    v-model="shadowModel"
    :class="{
      filled: Array.isArray(shadowModel) ? shadowModel.length > 0 : !!shadowModel,
    }"
    :size="primevueSize"
    :loading="props.loading === undefined ? searchFunctionRunning : props.loading"
  >
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}"></slot>
    </template>

    <template #clearicon="{ clearCallback }">
      <button
        aria-label="legen"
        :class="[
          '-mx-2 px-2',
          { 'absolute top-1/2 right-11.5 -translate-y-1/2': component === AutoComplete },
        ]"
        type="button"
        @click.stop="clearCallback"
      >
        <FontAwesomeIcon :icon="faXmark" />
      </button>
    </template>

    <template #option="slotProps" v-if="$slots.default">
      <slot v-bind="slotProps"></slot>
    </template>

    <!-- @vue-skip geeft error chipicon maar die is er gewoon altijd bij Autocomplete-->
    <template #chipicon="{ removeCallback }" v-if="component === AutoComplete">
      <a
        role="button"
        class="ml-2 h-3.5 cursor-pointer rounded-md leading-4 text-inherit"
        :class="{ hidden: props.disabled }"
        tabindex="0"
        @click="removeCallback"
        @keydown.enter="removeCallback"
        @keydown.space="removeCallback"
      >
        <FontAwesomeIcon :icon="['far', 'xmark']" />
      </a>
    </template>
  </component>

  <div v-else class="filled min-h-form-row min-w-0 items-center">
    {{ readonlyValue }}
  </div>
</template>
